<template>
    <div class="package-entry">
        <div class="intro has-text-centered">
            <h2 class="has-text-white">Build Your Professional Development Bundle</h2>
            <p>Bundle clock hours across any of our courses and save 33%. Choose how you'd like to get started.</p>
        </div>

        <div class="columns mt-5">
            <div class="column">
                <div class="path-card" @click="$emit('start-self')">
                    <div class="icon-circle"><i class="fad fa-pen-ruler"></i></div>
                    <div class="path-title">Build it myself</div>
                    <div class="path-text">Pick your clock hours, fill your bundle with courses, and enroll in minutes.</div>
                    <ul class="path-points">
                        <li><i class="fas fa-check"></i>Get started right away</li>
                        <li><i class="fas fa-check"></i>See what moving up your district's salary schedule is worth</li>
                        <li><i class="fas fa-check"></i>Choose your own clock hours, courses, and payment terms</li>
                        <li><i class="fas fa-check"></i>Earn now, pay later — earn your raise before payments start (requires completion and district processing)</li>
                    </ul>
                    <div class="path-cta">Start building<i class="fas fa-arrow-right ml-2"></i></div>
                </div>
            </div>
            <div class="column">
                <div class="path-card" @click="$emit('start-rep')">
                    <div class="icon-circle"><i class="fad fa-phone"></i></div>
                    <div class="path-title">Work with a representative</div>
                    <div class="path-text">A dedicated representative will contact you and build the right bundle with you.</div>
                    <ul class="path-points">
                        <li><i class="fas fa-check"></i>Very experienced in helping educators</li>
                        <li><i class="fas fa-check"></i>Helps you understand your needs and choose courses that fit</li>
                        <li><i class="fas fa-check"></i>Handles enrollment and payment setup for you</li>
                        <li><i class="fas fa-check"></i>More flexible financing — term, deferment, and full-bundle options</li>
                        <li><i class="fas fa-check"></i>Stays on as your direct contact to help you along the way</li>
                    </ul>
                    <div class="path-cta">Request a contact<i class="fas fa-arrow-right ml-2"></i></div>
                </div>
            </div>
        </div>

        <!-- How our courses work -->
        <div class="how-it-works mt-6">
            <div class="how-card">
                <h3 class="has-text-centered">How Our Courses Work</h3>
                <p class="how-sub has-text-centered">Many of our courses are individualized to meet your professional needs &mdash; think of it as an IEP designed for your own growth as an educator. The number of hours you choose determines the depth of study in your selected subject or grade level.</p>
                <div class="columns mt-4">
                    <div class="column how-step" v-for="(step, index) in howSteps" :key="'how-' + index">
                        <div class="step-circle">{{ index + 1 }}</div>
                        <div class="how-title">{{ step.title }}</div>
                        <div class="how-text">{{ step.text }}</div>
                    </div>
                </div>
            </div>
        </div>

        <div class="package-card mt-5">
            <package-faq :faqs="faqs"/>
        </div>
    </div>
</template>

<script>
    import PackageFaq from "./PackageFaq";
    import {ENTRY_FAQ} from "@/constants/packages";

    export default {
        name: "PackageEntry",
        components: {PackageFaq},
        data: function() {
            return {
                faqs: ENTRY_FAQ,
                howSteps: [
                    {
                        title: 'Define your goals',
                        text: 'Each course is built around the goals and objectives you select, individualizing the work to your professional needs.'
                    },
                    {
                        title: 'Adapt as you go',
                        text: 'You work from the goals and objectives you defined, shaping and adapting your study as you go.'
                    },
                    {
                        title: 'Document your progress',
                        text: 'Reflections and artifacts submitted at set intervals document your progress from where you began to where you finish.'
                    },
                    {
                        title: 'Time well spent',
                        text: 'When you complete a course, you\'ll have spent meaningful time on the objectives you need most.'
                    }
                ]
            };
        }
    };
</script>

<style lang="scss" scoped>
    .package-entry {
        max-width: 880px;
        margin: 3rem auto 0;

        .intro {
            h2 {
                font-size: 1.7rem;
                font-weight: 900;
            }

            p {
                color: #c7d6e6;
                margin-top: 0.5rem;
            }
        }

        .how-it-works {
            h3 {
                font-size: 1.25rem;
                font-weight: 900;
                color: #33475b;
            }

            .how-sub {
                color: #666d7a;
                font-size: 0.9rem;
                margin: 0.4rem auto 0;
                max-width: 640px;
            }

            .how-card {
                background: #ffffff;
                border-radius: 4px;
                box-shadow: 5px 5px 30px 0 rgba(0, 0, 0, 0.4);
                padding: 1.8rem 1.7rem;
            }

            .how-step {
                text-align: center;
                padding: 0.75rem 1rem;

                .step-circle {
                    width: 40px;
                    height: 40px;
                    border-radius: 50%;
                    background: #f2f7fc;
                    border: 2px solid #366ea5;
                    color: #366ea5;
                    font-weight: 900;
                    display: flex;
                    align-items: center;
                    justify-content: center;
                    margin: 0 auto 0.7rem;
                }

                .how-title {
                    font-size: 0.95rem;
                    font-weight: 900;
                    color: #33475b;
                }

                .how-text {
                    font-size: 0.82rem;
                    color: #666d7a;
                    margin-top: 0.4rem;
                }
            }
        }

        .path-card {
            background: #ffffff;
            border-radius: 4px;
            box-shadow: 5px 5px 30px 0 rgba(0, 0, 0, 0.4);
            border: 2px solid #ffffff;
            padding: 1.8rem 1.7rem;
            cursor: pointer;
            display: flex;
            flex-direction: column;
            height: 100%;

            &:hover {
                border-color: #7fa8cd;
            }

            .icon-circle {
                width: 48px;
                height: 48px;
                border-radius: 50%;
                background: #f2f7fc;
                display: flex;
                align-items: center;
                justify-content: center;

                i {
                    font-size: 1.3rem;
                    color: #2e5f8f;
                }
            }

            .path-title {
                font-size: 1.15rem;
                font-weight: 900;
                color: #33475b;
                margin-top: 1rem;
            }

            .path-text {
                font-size: 0.9rem;
                color: #666d7a;
                margin-top: 0.5rem;
            }

            .path-points {
                margin: 0.9rem 0 0;
                padding: 0;
                list-style: none;
                flex: 1;

                li {
                    font-size: 0.85rem;
                    color: #666d7a;
                    padding: 0.25rem 0;
                    display: flex;
                    gap: 0.6rem;

                    i {
                        color: #2e7d4f;
                        margin-top: 3px;
                        flex-shrink: 0;
                    }
                }
            }

            .path-cta {
                margin-top: 1.1rem;
                color: #366ea5;
                font-size: 0.9rem;
                font-weight: bold;
            }
        }
    }
</style>
